import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { getStatusColors, useThemeVersion } from '../../../../theme/chartTheme';
import { Box, Typography } from '@material-ui/core';
import type { BirdsEyeViewAnalytics } from '../../../../_models/schedule.model';

interface StatusDistributionChartProps {
  analytics: BirdsEyeViewAnalytics;
}

const StatusDistributionChart: React.FC<StatusDistributionChartProps> = ({ analytics }) => {
  const themeVersion = useThemeVersion();
  const statusColors = React.useMemo(() => getStatusColors(), [themeVersion]);
  const data = [
    {
      name: 'Scheduled',
      value: analytics.statusCounts.scheduled,
      color: statusColors.info,
    },
    {
      name: 'Conducted',
      value: analytics.statusCounts.conducted,
      color: statusColors.success,
    },
    {
      name: 'Cancelled',
      value: analytics.statusCounts.cancelled,
      color: statusColors.danger,
    },
  ];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const percentage = ((payload[0].value / analytics.totalSlots) * 100).toFixed(1);
      return (
        <div
          style={{
            background: 'var(--surface-raised)',
            padding: '12px 16px',
            border: '1px solid var(--border-default)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 4 }}>
            {payload[0].name}
          </Typography>
          <Typography variant="body2" style={{ color: 'var(--text-muted)' }}>
            {payload[0].value.toLocaleString()} sessions ({percentage}%)
          </Typography>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    // Only show percentage inside slice if it's significant (more than 10%)
    if (percent < 0.1) return null;

    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
    const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: '16px', fontWeight: 700 }}
      >
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  if (analytics.totalSlots === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <Typography variant="body1" style={{ color: 'var(--text-muted)' }}>
          No status data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <ResponsiveContainer width="100%" height={350}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={120}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            iconType="circle"
            formatter={(value, entry: any) => {
              const item = data.find((d) => d.name === value);
              return `${value} (${item?.value.toLocaleString() || 0})`;
            }}
          />
        </PieChart>
      </ResponsiveContainer>

      <Box mt={2} display="flex" justifyContent="space-around" flexWrap="wrap">
        {data.map((item) => {
          const percentage = ((item.value / analytics.totalSlots) * 100).toFixed(1);
          return (
            <Box
              key={item.name}
              style={{
                flex: '1 1 150px',
                minWidth: '150px',
                padding: '16px',
                margin: '8px',
                borderRadius: '12px',
                background: 'var(--surface-page)',
                border: `2px solid ${item.color}30`,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h4"
                style={{ color: item.color, fontWeight: 700, marginBottom: 4 }}
              >
                {percentage}%
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 4 }}>
                {item.name}
              </Typography>
              <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
                {item.value.toLocaleString()} sessions
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default StatusDistributionChart;
